import { ArticleSummary, PaginatedResult } from '../../shared/types';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';

export const SEARCH_ARTICLES = gql`
    query searchArticles(
        $params: ArticleSearchInput
        $first: Int
        $after: String
    ) {
        searchArticles(params: $params, first: $first, after: $after) {
            totalCount
            pageInfo {
                endCursor
                hasNextPage
            }
            documents {
                _id
                publishedBy {
                    _id
                    name
                    type
                }
                title
                summary
                categories {
                    _id
                    name
                }
                premiumChannelDetails {
                    _id
                    name
                }
                rootCategories {
                    _id
                    name
                }
                publishDate
                isPremium
                isPremiumActive
                isLocked
                canSeeFullVersion
                isPinned
            }
        }
    }
`;

interface UseArticleListOptions {
    batchSize: number;
    categoryId?: string;
    premiumChannelId?: string;
    dateFrom?: string;
    dateTo?: string;
    tagId?: string;
    deskId?: string;
    contributorId?: string;
    onlyMySubscriptions?: boolean;
    searchQuery?: string;
    freeOnly?: boolean;
    premiumOnly?: boolean;
    displayInChartSection?: boolean;
    pinnedFirst?: boolean;
}

export const useArticleList = ({
    categoryId,
    premiumChannelId,
    batchSize,
    dateFrom,
    dateTo,
    tagId,
    deskId,
    contributorId,
    onlyMySubscriptions,
    searchQuery,
    freeOnly,
    premiumOnly,
    displayInChartSection,
    pinnedFirst,
}: UseArticleListOptions) => {
    const params = {
        categoryId,
        premiumChannelId,
        dateFrom,
        dateTo,
        tagId,
        deskId,
        contributorId,
        onlyMySubscriptions,
        searchQuery,
        freeOnly,
        premiumOnly,
        displayInChartSection,
        pinnedFirst,
    };

    const query = useQuery<{ searchArticles: PaginatedResult<ArticleSummary> }>(
        SEARCH_ARTICLES,
        {
            fetchPolicy: 'network-only',
            variables: {
                first: batchSize,
                params,
            },
        }
    );

    const data = query.data?.searchArticles;

    const fetchMore = () => {
        query.fetchMore({
            variables: {
                after: data?.pageInfo.endCursor,
                first: batchSize,
                params,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;

                const mergedDocuments = [
                    ...prev.searchArticles.documents,
                    ...fetchMoreResult.searchArticles.documents,
                ];

                const dedupedDocuments = mergedDocuments.filter(
                    (article, index, list) =>
                        list.findIndex((item) => item._id === article._id) ===
                        index
                );
                return {
                    searchArticles: {
                        ...prev.searchArticles,

                        totalCount: prev.searchArticles.totalCount,
                        documents: dedupedDocuments,
                        pageInfo: {
                            ...fetchMoreResult.searchArticles.pageInfo,
                            endCursor:
                                fetchMoreResult.searchArticles.pageInfo
                                    .endCursor ??
                                prev.searchArticles.pageInfo.endCursor,
                        },
                    },
                };
            },
        });
    };

    const refetch = (options: UseArticleListOptions) => {
        const { batchSize, ...params } = options;
        return query.refetch({
            first: batchSize,
            params,
        });
    };

    return {
        loading: query.loading,
        articles: data?.documents,
        totalCount: data?.totalCount,
        hasNextPage: data?.pageInfo.hasNextPage,
        refetch,
        fetchMore,
    };
};
